/**
 * HQ — 📡 태블릿·게이트 (2026-10-28 대표 승인, 대표 전용)
 *
 * 매장마다 태블릿이 지금 어느 게이트 주소를 쓰고 연결됐는지 보고,
 * [매장 안 게이트 찾기] → 찾은 목록에서 고르거나 직접 입력 → [저장·적용].
 * 태블릿(앱 5.7.6~)은 다음 동기화(최대 90초) 때 새 주소로 연결 시험 → 되면 바꾸고,
 * 안 되면 옛 주소를 그대로 쓰며 '적용 실패(되돌림)'로 알려 온다.
 *
 * 🔴 확인 창에 window.confirm 쓰지 말 것 — 2단계 버튼으로 처리한다(CLAUDE.md).
 */
import React, { useCallback, useEffect, useState } from 'react';
import hqTabletGateService, {
  BypassItem,
  BypassSummary,
  FoundGate,
  GateCheck,
  HistoryItem,
  StoreDetail,
  StoreRow,
  TabletReport,
} from '../services/hqTabletGateService';

const ago = (sec: number | null | undefined) => {
  if (sec === null || sec === undefined) return '-';
  if (sec < 60) return `${sec}초 전`;
  if (sec < 3600) return `${Math.floor(sec / 60)}분 전`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}시간 전`;
  return `${Math.floor(sec / 86400)}일 전`;
};

const fmtKst = (v: string | number | null | undefined) => {
  if (v === null || v === undefined || v === '') return '-';
  const d = typeof v === 'number' ? new Date(v < 1e12 ? v * 1000 : v) : new Date(v);
  return d.toLocaleString('ko-KR', {
    timeZone: 'Asia/Seoul',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const shortId = (id?: string | null) => (id ? (id.length > 10 ? `${id.slice(0, 4)}…${id.slice(-4)}` : id) : '-');

const ROLE_LABEL: Record<string, string> = { entry: '입구', exit: '출구', both: '겸용' };

const GateBadge: React.FC<{ c?: GateCheck | null }> = ({ c }) => {
  if (!c || !c.ip) return <span className="text-slate-400">미설정</span>;
  return (
    <span>
      <span className="font-mono">{c.ip}</span>{' '}
      {c.ok ? (
        <span className="px-1.5 py-0.5 rounded bg-green-100 text-green-700 text-xs">연결됨</span>
      ) : (
        <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-xs">끊김</span>
      )}
    </span>
  );
};

const GateServerBadge: React.FC<{ g: StoreRow['gate_server'] }> = ({ g }) => {
  if (g.linked) return <span className="px-1.5 py-0.5 rounded bg-green-100 text-green-700 text-xs">서버 접속 중</span>;
  if (g.seconds_ago !== null && g.seconds_ago !== undefined)
    return <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-xs">끊김 · {ago(g.seconds_ago)}</span>;
  return <span className="text-slate-400 text-xs">서버 접속 기록 없음</span>;
};

const historyText = (h: HistoryItem) => {
  switch (h.kind) {
    case 'config_changed':
      return `${h.by || '본부'} — 입구 ${h.before?.entry_ip || '-'} → ${h.after?.entry_ip || '-'} · 출구 ${
        h.before?.exit_ip || '-'
      } → ${h.after?.exit_ip || '-'}${h.confirm_store_mismatch ? ' (다른 매장 게이트 경고 확인 후 저장)' : ''}`;
    case 'scan_requested':
      return `${h.by || '본부'} — 매장 안 게이트 찾기 요청`;
    case 'tablet_apply_ok':
      return `태블릿 ${shortId(h.device_id)} 적용 성공 — ${h.detail || ''}`;
    case 'tablet_apply_failed':
      return `태블릿 ${shortId(h.device_id)} 적용 실패(되돌림) — ${h.detail || ''}`;
    default:
      return h.kind;
  }
};

const BYPASS_RESULT: Record<string, { label: string; cls: string }> = {
  ok: { label: '서버로 열림', cls: 'text-amber-700' },
  not_linked: { label: '실패 — 게이트가 서버에 안 붙어 있음', cls: 'text-red-700' },
  not_taken: { label: '실패 — 게이트가 명령을 안 받아 감', cls: 'text-red-700' },
  busy: { label: '실패 — 서버 바쁨', cls: 'text-red-700' },
};

const SOURCE_LABEL: Record<string, string> = { face: '얼굴', code: '번호', phone: '번호' };

/** 매장 목록용 — 우회가 있었으면 눈에 띄게. 잦은 우회 = 태블릿이 쓰는 게이트 주소 문제 신호 */
const BypassBadge: React.FC<{ b: BypassSummary | null | undefined }> = ({ b }) => {
  if (!b || b.week === 0) return null;
  const cls = b.today_fail > 0 ? 'bg-red-100 text-red-700' : b.today > 0 ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-600';
  return (
    <div className={`mt-0.5 inline-block px-1.5 py-0.5 rounded text-xs ${cls}`}>
      서버 우회 오늘 {b.today}회{b.today_fail > 0 ? `(실패 ${b.today_fail})` : ''} · 7일 {b.week}회
    </div>
  );
};

const bypassText = (x: BypassItem) =>
  `${x.direction === 'exit' ? '퇴실' : '입실'}${x.source ? `(${SOURCE_LABEL[x.source] || x.source})` : ''} · 태블릿 ${shortId(
    x.device_id,
  )}${x.app_version ? ` v${x.app_version}` : ''} · 직접 열기 ${x.direct_ip || '-'} 실패(${x.direct_error || '-'}${
    x.direct_ms != null ? `, ${x.direct_ms}ms` : ''
  })${x.ms != null ? ` → 서버 ${x.ms}ms` : ''}`;

/**
 * 주소 세 가지를 나란히 — ①게이트가 스스로 알려 준 매장 안 주소 ②서버에 저장된 주소 ③태블릿이 실제로 쓰는 주소.
 * 저장된 주소와 다르면 빨간 칸. (2026-10-28 대표 지시 "내부 아이피·연결된 아이피를 구분하고 내가 직접 넣을 수 있어야")
 */
const AddressCompare: React.FC<{ d: StoreDetail; onUseReported: () => void }> = ({ d, onUseReported }) => {
  const rep = d.gate_server.reported_ips || {};
  const saved = { entry: d.saved.entry_ip || '', exit: d.saved.exit_ip || '' };
  const same = (a?: string | null, b?: string | null) => (a || '').trim().toLowerCase() === (b || '').trim().toLowerCase();
  const fresh = (d.tablets || []).filter((t) => t.fresh);
  const cell = (ip: string | null | undefined, savedIp: string, extra?: React.ReactNode) => {
    if (!ip) return <td className="px-2 py-1 text-slate-400">-</td>;
    const bad = !same(ip, savedIp);
    return (
      <td className={`px-2 py-1 font-mono whitespace-nowrap ${bad ? 'bg-red-50 text-red-700 font-semibold' : ''}`}>
        {ip}
        {extra}
        {bad && <span className="ml-1 font-sans text-xs">≠ 저장값</span>}
      </td>
    );
  };
  const mismatch = d.addr_mismatch.length > 0 || fresh.some((t) => !same(t.entry?.ip, saved.entry) || !same(t.exit?.ip, saved.exit));
  return (
    <div className="space-y-1">
      <table className="text-xs border rounded">
        <thead className="bg-slate-50 text-slate-500">
          <tr>
            <th className="text-left px-2 py-1">어느 주소</th>
            <th className="text-left px-2 py-1">입구</th>
            <th className="text-left px-2 py-1">출구</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-t">
            <td className="px-2 py-1">① 게이트가 알려 준 매장 안 주소</td>
            {d.gate_server.reports_ip === false ? (
              <td colSpan={2} className="px-2 py-1 text-slate-500">
                게이트가 안 알려 줌{d.gate_server.version ? ` (펌웨어 ${d.gate_server.version} — 와이파이형 1.9.4·유선형 v2.3 부터 알려 줌)` : ''}
              </td>
            ) : d.gate_server.reports_ip == null ? (
              <td colSpan={2} className="px-2 py-1 text-slate-400">게이트가 서버에 붙은 기록 없음</td>
            ) : (
              <>
                {cell(rep.entry, saved.entry)}
                {cell(rep.exit, saved.exit || saved.entry)}
              </>
            )}
          </tr>
          <tr className="border-t bg-slate-50/50">
            <td className="px-2 py-1">② 서버에 저장된 주소</td>
            <td className="px-2 py-1 font-mono">{saved.entry || '-'}</td>
            <td className="px-2 py-1 font-mono">{saved.exit || '-'}</td>
          </tr>
          {fresh.map((t) => (
            <tr key={t.device_id} className="border-t">
              <td className="px-2 py-1">
                ③ 태블릿 {shortId(t.device_id)}
                {t.tablet_role ? ` (${ROLE_LABEL[t.tablet_role] || t.tablet_role})` : ''}이 쓰는 주소
              </td>
              {cell(
                t.entry?.ip,
                saved.entry,
                t.entry?.ip ? <span className={`ml-1 font-sans ${t.entry.ok ? 'text-green-700' : 'text-red-700'}`}>{t.entry.ok ? '연결됨' : '끊김'}</span> : null,
              )}
              {cell(
                t.exit?.ip,
                saved.exit,
                t.exit?.ip ? <span className={`ml-1 font-sans ${t.exit.ok ? 'text-green-700' : 'text-red-700'}`}>{t.exit.ok ? '연결됨' : '끊김'}</span> : null,
              )}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="text-xs text-slate-400">({fmtKst(d.saved.updated_at)} 저장 · 태블릿은 5.7.6 부터 보고)</div>
      {mismatch && (
        <div className="p-2 rounded bg-red-50 border border-red-300 text-red-800 text-xs">
          ⚠️ 주소가 서로 다릅니다. 태블릿·원장 PC [게이트 열기]·다음 로그인은 <b>② 저장된 주소</b>를 씁니다.
        </div>
      )}
      {d.addr_mismatch.length > 0 && (
        <button onClick={onUseReported} className="px-3 py-1 rounded bg-red-600 text-white text-xs">
          게이트가 알려 준 주소로 저장 (확인 단계로)
        </button>
      )}
    </div>
  );
};

/** 서버 우회 열기 기록 — 매장 안 직접 열기 실패 때만 생긴다. 잦으면 주소 문제 */
const BypassSection: React.FC<{ b: BypassSummary | null; redisOk: boolean }> = ({ b, redisOk }) => (
  <section className="border-t pt-3">
    <h3 className="font-semibold mb-1">서버 우회 열기 (매장 안 직접 열기 실패 때만)</h3>
    {!redisOk || !b ? (
      <div className="text-amber-700 text-xs">상태 모름</div>
    ) : b.week === 0 ? (
      <div className="text-slate-400 text-xs">최근 7일 우회 없음 — 태블릿이 매장 안 주소로 정상 열고 있습니다</div>
    ) : (
      <div className="space-y-1 text-xs">
        <div className={b.today > 0 ? 'p-2 rounded bg-amber-50 border border-amber-300 text-amber-900' : ''}>
          오늘 <b>{b.today}회</b>
          {b.today_fail > 0 && <b className="text-red-700"> (실패 {b.today_fail}회)</b>} · 최근 7일 {b.week}회
          {b.today > 0 && <div>태블릿이 매장 안 게이트 주소로 문을 못 열고 있습니다. 위 주소 비교를 확인하세요.</div>}
        </div>
        <div className="text-slate-500">
          {b.per_day
            .filter((x) => x.ok + x.fail > 0)
            .map((x) => `${x.date.slice(5)} ${x.ok + x.fail}회${x.fail ? `(실패 ${x.fail})` : ''}`)
            .join(' · ')}
        </div>
        <ul className="max-h-48 overflow-y-auto space-y-0.5">
          {(b.log || []).map((x, i) => {
            const r = BYPASS_RESULT[x.result] || { label: x.result, cls: '' };
            return (
              <li key={`${x.at}-${i}`}>
                <span className="text-slate-400 mr-1">{fmtKst(x.at)}</span>
                <span className={`${r.cls} font-semibold mr-1`}>{r.label}</span>
                {bypassText(x)}
              </li>
            );
          })}
        </ul>
      </div>
    )}
  </section>
);

const StoreDetailPanel: React.FC<{ orgId: string; onClose: () => void; onSaved: () => void }> = ({
  orgId,
  onClose,
  onSaved,
}) => {
  const [d, setD] = useState<StoreDetail | null>(null);
  const [err, setErr] = useState('');
  const [entryIp, setEntryIp] = useState('');
  const [exitIp, setExitIp] = useState('');
  const [touched, setTouched] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [mismatch, setMismatch] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    hqTabletGateService
      .getStore(orgId)
      .then((x) => {
        setD(x);
        setErr('');
      })
      .catch((e) => setErr(e?.response?.data?.detail || '불러오지 못했습니다'));
  }, [orgId]);

  useEffect(() => {
    setD(null);
    setTouched(false);
    setConfirming(false);
    setMismatch(null);
    setMsg('');
    load();
    const t = setInterval(load, 10000); // 태블릿 보고가 90초마다라 10초면 충분
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (d && !touched) {
      setEntryIp(d.saved.entry_ip || '');
      setExitIp(d.saved.exit_ip || '');
    }
  }, [d, touched]);

  if (err) return <div className="bg-white rounded-xl border p-4 text-red-600">{err}</div>;
  if (!d) return <div className="bg-white rounded-xl border p-4 text-slate-500">불러오는 중…</div>;

  const pick = (slot: 'entry' | 'exit', ip: string) => {
    setTouched(true);
    setConfirming(false);
    setMismatch(null);
    if (slot === 'entry') setEntryIp(ip);
    else setExitIp(ip);
  };

  // [게이트가 알려 준 주소로 저장] — 칸을 채우고 바로 확인 단계(바뀌기 전→후)로
  const applyReported = () => {
    const rep = d.gate_server.reported_ips || {};
    setTouched(true);
    setMismatch(null);
    const savedEntry = d.saved.entry_ip || '';
    // 출구 게이트만 알려 왔으면 출구 칸으로(입구 칸이 비어 있을 때만 입구로 — 하나만 쓰는 매장)
    const nextEntry = rep.entry || savedEntry || rep.exit || '';
    setEntryIp(nextEntry);
    setExitIp(rep.exit && nextEntry !== rep.exit ? rep.exit : d.saved.exit_ip || '');
    setConfirming(true);
  };

  const chosenMismatch = (d.scan_results?.found || []).filter(
    (g) => g.store_match === false && [entryIp.trim(), exitIp.trim()].includes(g.ip),
  );

  const requestScan = async () => {
    setBusy(true);
    setMsg('');
    try {
      await hqTabletGateService.requestScan(orgId);
      setMsg('찾기를 요청했습니다. 태블릿이 다음 동기화(최대 90초) 때 찾기 시작하고, 찾는 데 30~60초 걸립니다.');
      load();
    } catch (e: any) {
      setMsg(e?.response?.data?.detail || '요청하지 못했습니다');
    } finally {
      setBusy(false);
    }
  };

  const save = async (confirmMismatch: boolean) => {
    setBusy(true);
    setMsg('');
    try {
      const r = await hqTabletGateService.saveConfig(orgId, {
        entry_ip: entryIp.trim(),
        exit_ip: exitIp.trim(),
        confirm_store_mismatch: confirmMismatch,
      });
      setConfirming(false);
      setMismatch(null);
      setTouched(false);
      setMsg(
        r.pushed
          ? '저장했습니다. 태블릿이 다음 동기화(최대 90초) 때 새 주소로 연결을 시험하고 결과를 알려 옵니다.'
          : r.message || '저장했지만 태블릿에 바로 보내지 못했습니다.',
      );
      load();
      onSaved();
    } catch (e: any) {
      const det = e?.response?.data?.detail;
      if (e?.response?.status === 409 && det?.code === 'STORE_MISMATCH') {
        setMismatch(det.message);
      } else {
        setMsg(typeof det === 'string' ? det : '저장하지 못했습니다');
      }
    } finally {
      setBusy(false);
    }
  };

  const changed = entryIp.trim() !== (d.saved.entry_ip || '') || exitIp.trim() !== (d.saved.exit_ip || '');
  // 저장된 주소와 태블릿이 실제로 쓰는 주소가 다른 태블릿 — 적용 실패로 옛 주소를 쓰고 있거나,
  // 태블릿 설정 화면에서 직접 바꾼 경우. 새 설정을 아직 안 받은 태블릿(90초 이내)은 뺀다.
  const driftTablets = (d.tablets || []).filter(
    (t) =>
      t.fresh &&
      (!d.saved.config_version || t.applied_version === d.saved.config_version) &&
      ((t.entry?.ip || '') !== (d.saved.entry_ip || '') || (t.exit?.ip || '') !== (d.saved.exit_ip || '')),
  );
  const scanFound: FoundGate[] = d.scan_results?.found || [];

  return (
    <div className="bg-white rounded-xl border p-4 text-sm space-y-4">
      <div className="flex items-center justify-between">
        <b className="text-base">{d.organization_name}</b>
        <button className="text-slate-400" onClick={onClose}>
          닫기 ✕
        </button>
      </div>

      {!d.redis_ok && (
        <div className="p-2 rounded bg-amber-50 border border-amber-200 text-amber-800">
          ⚠️ 상태 저장소(Redis)에 연결되지 않아 태블릿 상태를 <b>모릅니다</b>. 찾기·즉시 적용도 안 됩니다.
        </div>
      )}

      <section>
        <h3 className="font-semibold mb-1">게이트 장치</h3>
        <div className="space-y-1">
          <div>
            서버 접속: <GateServerBadge g={d.gate_server} />
            {Object.keys(d.gate_server.roles || {}).length > 1 ? (
              <span className="ml-2 text-slate-500">
                {Object.entries(d.gate_server.roles || {})
                  .map(([r, v]) => `${ROLE_LABEL[r] || r} 게이트 펌웨어 ${v.version || '?'} (${ago(v.seconds_ago)})`)
                  .join(' · ')}
              </span>
            ) : (
              d.gate_server.version && <span className="ml-2 text-slate-500">펌웨어 {d.gate_server.version}</span>
            )}
          </div>
          <AddressCompare d={d} onUseReported={applyReported} />
          {driftTablets.length > 0 && (
            <div className="p-2 rounded bg-amber-50 border border-amber-300 text-amber-900 text-xs space-y-1">
              <div>
                ⚠️ 저장된 주소와 태블릿이 지금 쓰는 주소가 다릅니다. 태블릿이 새 주소 적용에 실패해 옛 주소를 쓰고 있거나,
                태블릿 설정 화면에서 직접 바꾼 경우입니다. <b>다음 로그인과 원장 PC [게이트 열기]는 저장된 주소를 씁니다.</b>
              </div>
              {driftTablets.map((t) => (
                <div key={t.device_id}>
                  · {shortId(t.device_id)}: 입구 <span className="font-mono">{t.entry?.ip || '-'}</span>
                  {t.entry?.ip ? (t.entry.ok ? ' (연결됨)' : ' (끊김)') : ''} · 출구{' '}
                  <span className="font-mono">{t.exit?.ip || '-'}</span>
                  {t.exit?.ip ? (t.exit.ok ? ' (연결됨)' : ' (끊김)') : ''}
                  <button
                    className="ml-2 text-blue-600 underline"
                    onClick={() => {
                      pick('entry', t.entry?.ip || '');
                      pick('exit', t.exit?.ip || '');
                    }}
                  >
                    이 태블릿이 쓰는 주소로 칸 채우기
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section>
        <h3 className="font-semibold mb-1">태블릿 {d.tablets ? `${d.tablets.length}대` : ''}</h3>
        {d.tablets === null ? (
          <div className="text-amber-700">상태 모름</div>
        ) : d.tablets.length === 0 ? (
          <div className="text-slate-500">
            보고한 태블릿이 없습니다. 상태 보고는 앱 <b>5.7.6</b>부터 보냅니다.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="text-left px-2 py-1">기기</th>
                  <th className="text-left px-2 py-1">앱</th>
                  <th className="text-left px-2 py-1">와이파이</th>
                  <th className="text-left px-2 py-1">입구 게이트</th>
                  <th className="text-left px-2 py-1">출구 게이트</th>
                  <th className="text-left px-2 py-1">마지막 보고</th>
                  <th className="text-left px-2 py-1">마지막 적용</th>
                </tr>
              </thead>
              <tbody>
                {d.tablets.map((t: TabletReport) => (
                  <tr key={t.device_id} className={`border-t ${t.fresh ? '' : 'text-slate-400'}`}>
                    <td className="px-2 py-1 whitespace-nowrap" title={t.device_id}>
                      {shortId(t.device_id)}
                      {t.tablet_role && <span className="ml-1">({ROLE_LABEL[t.tablet_role] || t.tablet_role})</span>}
                      {t.scanning && <span className="ml-1 text-blue-600">찾는 중…</span>}
                    </td>
                    <td className="px-2 py-1">{t.app_version || '-'}</td>
                    <td className="px-2 py-1 whitespace-nowrap">
                      <span className="font-mono">{t.wifi_ip || '-'}</span>
                      {t.wifi_ssid && <div className="text-slate-500">{t.wifi_ssid}</div>}
                    </td>
                    <td className="px-2 py-1 whitespace-nowrap">
                      <GateBadge c={t.entry} />
                    </td>
                    <td className="px-2 py-1 whitespace-nowrap">
                      <GateBadge c={t.exit} />
                    </td>
                    <td className="px-2 py-1 whitespace-nowrap">
                      {ago(t.seconds_ago)}
                      {!t.fresh && <div className="text-red-600">보고 끊김</div>}
                    </td>
                    <td className="px-2 py-1 min-w-[160px]">
                      {t.last_apply?.version ? (
                        <>
                          {t.last_apply.ok ? (
                            <span className="text-green-700">적용 성공</span>
                          ) : (
                            <span className="text-red-700">적용 실패(되돌림)</span>
                          )}
                          <span className="ml-1 text-slate-400">{fmtKst(t.last_apply.at || null)}</span>
                          {d.saved.config_version && t.applied_version !== d.saved.config_version && (
                            <div className="text-amber-700">새 설정 기다리는 중</div>
                          )}
                          <div className="text-slate-500">{t.last_apply.detail}</div>
                        </>
                      ) : d.saved.config_version && t.applied_version !== d.saved.config_version ? (
                        <span className="text-amber-700">새 설정 기다리는 중</span>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {d.old_app_tablets.length > 0 && (
          <div className="mt-2 text-xs text-slate-500">
            상태 보고가 안 오는 태블릿 — 켜짐 기록만 있음(최근 14일, 5.7.5 이하 옛 앱이거나 보고가 막힌 경우):{' '}
            {d.old_app_tablets.map((o) => `${shortId(o.device_id)} v${o.app_version || '?'} (${fmtKst(o.last_at)})`).join(' · ')}
          </div>
        )}
      </section>

      <section>
        <div className="flex items-center gap-2 mb-1">
          <h3 className="font-semibold">매장 안 게이트 찾기</h3>
          <button
            disabled={busy || !d.redis_ok}
            onClick={requestScan}
            className="px-3 py-1 rounded bg-slate-800 text-white text-xs disabled:opacity-40"
          >
            🔎 매장 안 게이트 찾기
          </button>
        </div>
        {d.scan ? (
          <div className="text-xs text-slate-500 mb-1">
            {fmtKst(d.scan.at)} {d.scan.requested_by} 요청 · 응답한 태블릿 {d.scan_results?.tablets.length || 0}대
            {(d.scan_results?.tablets || []).map((t) => (
              <div key={t.device_id}>
                · {shortId(t.device_id)} ({t.wifi_ip || '-'}): {t.count}개 찾음 · 훑은 대역 {(t.subnets || []).join(', ') || '-'}
                {t.error && <span className="text-red-600"> · 오류 {t.error}</span>}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-xs text-slate-500 mb-1">최근 찾기 요청 없음</div>
        )}
        {scanFound.length > 0 && (
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="text-left px-2 py-1">주소</th>
                <th className="text-left px-2 py-1">이름(mDNS)</th>
                <th className="text-left px-2 py-1">역할</th>
                <th className="text-left px-2 py-1">게이트 매장 이름</th>
                <th className="px-2 py-1"></th>
              </tr>
            </thead>
            <tbody>
              {scanFound.map((g) => (
                <tr key={g.ip} className={`border-t ${g.store_match === false ? 'bg-red-50' : ''}`}>
                  <td className="px-2 py-1 font-mono">{g.ip}</td>
                  <td className="px-2 py-1">{g.mdns || '-'}</td>
                  <td className="px-2 py-1">{g.role ? ROLE_LABEL[g.role] || g.role : '-'}</td>
                  <td className="px-2 py-1">
                    {g.store_name || <span className="text-slate-400">없음</span>}
                    {g.store_match === false && (
                      <div className="text-red-700 font-semibold">⚠️ 이 매장 게이트가 아닌 것 같습니다 — 게이트에 넣어 둔 매장이 다름</div>
                    )}
                  </td>
                  <td className="px-2 py-1 whitespace-nowrap">
                    <button className="text-blue-600 underline mr-2" onClick={() => pick('entry', g.ip)}>
                      입구로
                    </button>
                    <button className="text-blue-600 underline" onClick={() => pick('exit', g.ip)}>
                      출구로
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="border-t pt-3">
        <h3 className="font-semibold mb-2">게이트 주소 바꾸기</h3>
        <div className="grid grid-cols-[60px_1fr] gap-2 items-center max-w-md">
          <label className="text-slate-500">입구</label>
          <input
            className="border rounded px-2 py-1 font-mono"
            value={entryIp}
            placeholder="예: 192.168.219.62"
            onChange={(e) => {
              setTouched(true);
              setConfirming(false);
              setMismatch(null);
              setEntryIp(e.target.value);
            }}
          />
          <label className="text-slate-500">출구</label>
          <input
            className="border rounded px-2 py-1 font-mono"
            value={exitIp}
            placeholder="하나만 쓰는 매장은 비워 두기"
            onChange={(e) => {
              setTouched(true);
              setConfirming(false);
              setMismatch(null);
              setExitIp(e.target.value);
            }}
          />
        </div>
        {chosenMismatch.length > 0 && !mismatch && (
          <div className="mt-2 p-2 rounded bg-red-50 border border-red-200 text-red-700 text-xs">
            ⚠️ 고른 주소의 게이트가 이 매장 것이 아닌 것 같습니다:{' '}
            {chosenMismatch.map((g) => `${g.ip}(${g.store_name || '이름 없음'})`).join(', ')}
          </div>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {!confirming ? (
            <button
              disabled={busy || !changed}
              onClick={() => setConfirming(true)}
              className="px-3 py-1.5 rounded bg-blue-600 text-white disabled:opacity-40"
            >
              저장·적용
            </button>
          ) : (
            <>
              <span className="text-xs">
                입구 <b className="font-mono">{d.saved.entry_ip || '-'}</b> → <b className="font-mono">{entryIp.trim() || '-'}</b>
                {' · '}출구 <b className="font-mono">{d.saved.exit_ip || '-'}</b> →{' '}
                <b className="font-mono">{exitIp.trim() || '-'}</b>
              </span>
              <button
                disabled={busy}
                onClick={() => save(false)}
                className="px-3 py-1.5 rounded bg-blue-600 text-white disabled:opacity-40"
              >
                이대로 적용
              </button>
              <button className="px-3 py-1.5 rounded border" onClick={() => setConfirming(false)}>
                취소
              </button>
            </>
          )}
          {touched && (
            <button
              className="text-xs text-slate-500 underline"
              onClick={() => {
                setTouched(false);
                setConfirming(false);
                setMismatch(null);
              }}
            >
              저장된 값으로 되돌리기
            </button>
          )}
        </div>
        {mismatch && (
          <div className="mt-2 p-2 rounded bg-red-50 border border-red-300 text-red-800 text-xs space-y-2">
            <div>⚠️ {mismatch}</div>
            <div className="flex gap-2">
              <button
                disabled={busy}
                onClick={() => save(true)}
                className="px-3 py-1 rounded bg-red-600 text-white disabled:opacity-40"
              >
                그래도 이 매장 게이트가 맞습니다 — 적용
              </button>
              <button className="px-3 py-1 rounded border" onClick={() => setMismatch(null)}>
                취소
              </button>
            </div>
          </div>
        )}
        {msg && <div className="mt-2 text-xs text-slate-700">{msg}</div>}
        <div className="mt-2 text-xs text-slate-500">
          적용은 태블릿이 새 주소로 연결을 시험해 보고 되면 바꿉니다. 안 되면 옛 주소를 그대로 쓰고 '적용 실패(되돌림)'로 알려 옵니다.
          원장 PC의 [게이트 열기] 버튼과 다음 로그인 때도 이 주소를 씁니다.
        </div>
      </section>

      <BypassSection b={d.bypass} redisOk={d.redis_ok} />

      <section className="border-t pt-3">
        <h3 className="font-semibold mb-1">최근 변경 기록</h3>
        {d.history === null ? (
          <div className="text-amber-700 text-xs">상태 모름</div>
        ) : d.history.length === 0 ? (
          <div className="text-slate-400 text-xs">기록 없음</div>
        ) : (
          <ul className="text-xs space-y-1 max-h-60 overflow-y-auto">
            {d.history.map((h, i) => (
              <li
                key={`${h.at}-${i}`}
                className={h.kind === 'tablet_apply_failed' ? 'text-red-700' : h.kind === 'tablet_apply_ok' ? 'text-green-700' : ''}
              >
                <span className="text-slate-400 mr-1">{fmtKst(h.at)}</span>
                {historyText(h)}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

const HqTabletGatesPage: React.FC = () => {
  const [data, setData] = useState<{ redis_ok: boolean; stores: StoreRow[] } | null>(null);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<string | null>(null);

  const load = useCallback(() => {
    hqTabletGateService
      .listStores()
      .then((d) => {
        setData(d);
        setError('');
      })
      .catch((e) => setError(e?.response?.data?.detail || '불러오지 못했습니다'));
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  if (error) return <div className="p-6 text-red-600">{error}</div>;
  if (!data) return <div className="p-6 text-slate-500">불러오는 중…</div>;

  return (
    <div className="p-4 sm:p-6 max-w-7xl">
      <h1 className="text-xl sm:text-2xl font-bold mb-1">📡 태블릿·게이트</h1>
      <p className="text-xs text-slate-500 mb-4">
        매장 태블릿이 지금 쓰는 게이트 주소와 연결 상태입니다(앱 5.7.6부터 90초마다 보고). 매장을 누르면 게이트를 찾고 주소를 바꿀 수 있습니다.
      </p>
      {!data.redis_ok && (
        <div className="mb-3 p-2 rounded bg-amber-50 border border-amber-200 text-amber-800 text-sm">
          ⚠️ 상태 저장소(Redis)에 연결되지 않아 태블릿 상태를 <b>모릅니다</b>.
        </div>
      )}
      <div className="flex flex-col xl:flex-row gap-4">
        <div className="bg-white rounded-xl border overflow-x-auto xl:w-[48%] xl:shrink-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs">
              <tr>
                <th className="text-left px-3 py-2">매장</th>
                <th className="text-left px-3 py-2">게이트 장치</th>
                <th className="text-left px-3 py-2">저장된 주소</th>
                <th className="text-left px-3 py-2">태블릿</th>
              </tr>
            </thead>
            <tbody>
              {data.stores.map((s) => (
                <tr
                  key={s.organization_id}
                  onClick={() => setSelected(s.organization_id)}
                  className={`border-t cursor-pointer hover:bg-slate-50 ${
                    selected === s.organization_id ? 'ring-2 ring-inset ring-slate-400' : ''
                  } ${s.tablets && s.tablets.gate_down > 0 ? 'bg-red-50' : ''}`}
                >
                  <td className="px-3 py-2 min-w-[130px]">{s.organization_name}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <GateServerBadge g={s.gate_server} />
                    {s.gate_server.reported_ip ? (
                      <div className={`text-xs font-mono ${s.addr_mismatch.length ? 'text-red-700 font-semibold' : 'text-slate-500'}`}>
                        알려 준 주소 {s.gate_server.reported_ip}
                      </div>
                    ) : s.gate_server.reports_ip === false ? (
                      <div className="text-xs text-slate-400">주소 안 알려 줌</div>
                    ) : null}
                    <BypassBadge b={s.bypass} />
                  </td>
                  <td className="px-3 py-2 text-xs font-mono whitespace-nowrap">
                    {s.saved.entry_ip || '-'}
                    {s.saved.exit_ip && <div>{s.saved.exit_ip}</div>}
                    {(s.addr_mismatch.length > 0 || (s.tablets?.drift || 0) > 0) && (
                      <div className="font-sans text-red-700 font-semibold">⚠️ 주소 불일치</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">
                    {s.tablets === null ? (
                      <span className="text-amber-700">상태 모름</span>
                    ) : s.tablets.total === 0 ? (
                      <span className="text-slate-400">보고 없음</span>
                    ) : (
                      <>
                        {s.tablets.fresh}/{s.tablets.total}대 보고 중
                        {s.tablets.gate_down > 0 && <div className="text-red-700">게이트 끊김 {s.tablets.gate_down}대</div>}
                        {s.tablets.gate_ok > 0 && <div className="text-green-700">연결됨 {s.tablets.gate_ok}대</div>}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex-1">
          {selected ? (
            <StoreDetailPanel orgId={selected} onClose={() => setSelected(null)} onSaved={load} />
          ) : (
            <div className="text-slate-400 text-sm p-4">매장을 고르세요</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HqTabletGatesPage;
